import { type InvoiceRow, GstSlabSchema, InvoiceStatusSchema } from '@utazzo/shared';
import {
  UzButton,
  UzDatePicker,
  UzDateRangePicker,
  UzDialog,
  UzInput,
  UzSelect,
  type UzDateRange,
} from '@utazzo/ui';
import { ChevronDown, ChevronRight, Download, FileCheck2, FileText, Plus } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { fetchAllInvoicesForExport } from '../invoices.api';
import { formatDateDMY, formatInrPaise, StatusBadge, statusLabel } from '../invoices.helpers';
import {
  useBankAccountOptions,
  useConvertManyToTaxInvoice,
  useConvertToTaxInvoice,
  useGenerateInvoice,
  useInvoiceAgents,
  useInvoicesList,
} from '../use-invoices';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { downloadPdf } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';
import { useCanExportFinanceData } from '@/features/auth/use-auth';
import { useDataGridState } from '@/hooks/use-data-grid-state';

const FARE_TYPE_LABEL: Record<number, string> = { 1: 'One Way', 2: 'Return', 3: 'MultiCity' };

/**
 * Admin/finance list of GST tax invoices generated from bookings. Server-side
 * pagination, status filter and a createdAt date range. Row click opens the
 * full invoice sheet; the header action generates a new invoice from a booking
 * number.
 */

// All filters are multi-select clearable chips rendered below the search bar.
// Agent + bank options are data-driven, so the full list is built in-component.
const STATUS_OPTIONS = InvoiceStatusSchema.options.map((s) => ({
  value: s,
  label: statusLabel(s),
}));
const SLAB_OPTIONS = GstSlabSchema.options.map((s) => ({ value: s, label: s }));
const PLACE_OPTIONS = [
  { value: 'INTRA', label: 'Intra-state' },
  { value: 'INTER', label: 'Inter-state' },
  { value: 'EXPORT', label: 'Export' },
];
const CUSTOMER_TYPE_OPTIONS = [
  { value: '', label: 'All customer types' },
  { value: 'B2C', label: 'B2C' },
  { value: 'B2B', label: 'B2B' },
];

const FILTER_IDS = ['status', 'gstSlab', 'placeOfSupply', 'customerType', 'agent', 'bank'] as const;

const startOfDayIso = (d: Date): string => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};
const endOfDayIso = (d: Date): string => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

/**
 * Financial-year helpers. The list defaults to the current Indian FY
 * (1 Apr - 31 Mar) by a createdAt range, which is robust across the
 * inconsistent `financialYear` string formats in the data (legacy "2026-27"
 * vs live "2627"). Every legacy invoice's createdAt is its real booking date.
 */
const fyStartYear = (d: Date): number =>
  d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
const fyLabel = (startYear: number): string =>
  `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
const fyRange = (startYear: number): UzDateRange => ({
  from: new Date(startYear, 3, 1), // 1 Apr
  to: new Date(startYear + 1, 2, 31), // 31 Mar
});
const CURRENT_FY_START = fyStartYear(new Date());
/** Current FY + the previous 5, newest first, plus an "All time" escape. */
const FY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  ...Array.from({ length: 6 }, (_, i) => {
    const start = CURRENT_FY_START - i;
    return { value: String(start), label: `FY ${fyLabel(start)}` };
  }),
  { value: 'all', label: 'All time' },
];

/** Sum of the three GST components (paise strings) formatted as INR. */
const gstTotalInr = (row: InvoiceRow): string => {
  const total = Number(row.cgstPaise) + Number(row.sgstPaise) + Number(row.igstPaise);
  return formatInrPaise(String(total));
};

/** One label/value pair in the expanded details panel. */
const Cell = ({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
  tone?: 'profit' | 'loss';
}): React.JSX.Element => (
  <div className="min-w-0">
    <dt className="text-[11px] font-medium tracking-wide text-zinc-400 uppercase">{label}</dt>
    <dd
      className={[
        'truncate text-sm tabular-nums',
        strong ? 'font-semibold text-zinc-900' : 'text-zinc-700',
        tone === 'profit' ? 'text-emerald-700' : '',
        tone === 'loss' ? 'text-red-600' : '',
      ].join(' ')}
    >
      {value}
    </dd>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div>
    <p className="mb-2 text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">{title}</p>
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">{children}</dl>
  </div>
);

const dash = (v: string | null | undefined): string => (v && v.trim() ? v : '-');
const dateOrDash = (iso: string | null): string => (iso ? formatDateDMY(iso) : '-');

/** Everything on the invoice, shown when a row is expanded. */
const InvoiceRowDetails = ({
  row,
  onConvert,
  converting,
}: {
  row: InvoiceRow;
  onConvert: (id: string) => void;
  converting: boolean;
}): React.JSX.Element => {
  const profit = Number(row.profitPaise);
  return (
    <div className="space-y-5 bg-zinc-50/60 px-6 py-5">
      {row.status === 'PROFORMA' && (
        <div className="flex justify-end">
          <UzButton
            size="sm"
            leftIcon={<FileCheck2 className="h-4 w-4" />}
            loading={converting}
            onClick={() => onConvert(row.id)}
          >
            Convert to tax invoice
          </UzButton>
        </div>
      )}
      <Section title="Numbers">
        <Cell label="Proforma No." value={dash(row.proformaNumber)} />
        <Cell label="Tax Invoice No." value={dash(row.taxInvoiceNumber)} />
        <Cell label="Financial Year" value={row.financialYear} />
        <Cell label="Place of Supply" value={row.placeOfSupply} />
      </Section>

      <Section title="Pricing">
        <Cell label="Taxable / Sale" value={formatInrPaise(row.grossSalePaise)} />
        <Cell label="CGST" value={formatInrPaise(row.cgstPaise)} />
        <Cell label="SGST" value={formatInrPaise(row.sgstPaise)} />
        <Cell label="IGST" value={formatInrPaise(row.igstPaise)} />
        <Cell label="TCS" value={formatInrPaise(row.tcsPaise)} />
        <Cell label="GST total" value={gstTotalInr(row)} />
        <Cell label="Grand total" value={formatInrPaise(row.grandTotalPaise)} strong />
      </Section>

      <Section title="Payment & margin">
        <Cell label="Paid" value={formatInrPaise(row.amountPaidPaise)} />
        <Cell label="Due" value={formatInrPaise(row.amountDuePaise)} strong />
        <Cell label="Supplier cost" value={formatInrPaise(row.supplierCostPaise)} />
        <Cell
          label="Profit / margin"
          value={formatInrPaise(row.profitPaise)}
          strong
          tone={profit < 0 ? 'loss' : 'profit'}
        />
      </Section>

      <Section title="Trip & agent">
        <Cell label="Agent" value={dash(row.travelAgentName)} />
        <Cell label="Customer GSTIN" value={dash(row.buyerGstin)} />
        <Cell
          label="Fare type"
          value={row.journeyType ? (FARE_TYPE_LABEL[row.journeyType] ?? '-') : '-'}
        />
        <Cell label="Departure" value={dash(row.departure)} />
        <Cell label="Destination" value={dash(row.destination)} />
        <Cell label="Travel date" value={dateOrDash(row.departureDate)} />
        <Cell label="Return date" value={dateOrDash(row.returnDate)} />
        <Cell label="Pax" value={row.noOfPax ?? '-'} />
        <Cell label="Hotel" value={dash(row.hotelName)} />
        <Cell label="Region" value={dash(row.region)} />
        <Cell label="Source" value={dash(row.querySource)} />
        <Cell label="HSN" value={dash(row.hsn)} />
      </Section>
    </div>
  );
};

/** Rupees (number, for spreadsheet math) from a paise string. */
const rupees = (paise: string): number => Number(paise) / 100;

/** Build + download an .xlsx of the given invoice rows (all columns). */
const exportInvoicesToXlsx = (rows: InvoiceRow[]): void => {
  const data = rows.map((r) => ({
    'Invoice No.': r.invoiceNumber,
    'Proforma No.': r.proformaNumber ?? '',
    'Tax Invoice No.': r.taxInvoiceNumber ?? '',
    Date: formatDateDMY(r.createdAt),
    Status: statusLabel(r.status),
    Customer: r.customerName ?? '',
    'Customer GSTIN': r.buyerGstin ?? '',
    Agent: r.travelAgentName ?? '',
    Slab: r.gstSlab,
    'Place of Supply': r.placeOfSupply,
    'Taxable / Sale': rupees(r.grossSalePaise),
    CGST: rupees(r.cgstPaise),
    SGST: rupees(r.sgstPaise),
    IGST: rupees(r.igstPaise),
    TCS: rupees(r.tcsPaise),
    'Grand Total': rupees(r.grandTotalPaise),
    Paid: rupees(r.amountPaidPaise),
    Due: rupees(r.amountDuePaise),
    'Supplier Cost': rupees(r.supplierCostPaise),
    Profit: rupees(r.profitPaise),
    'Fare Type': r.journeyType ? (FARE_TYPE_LABEL[r.journeyType] ?? '') : '',
    Departure: r.departure ?? '',
    Destination: r.destination ?? '',
    'Travel Date': r.departureDate ? formatDateDMY(r.departureDate) : '',
    'Return Date': r.returnDate ? formatDateDMY(r.returnDate) : '',
    Pax: r.noOfPax ?? '',
    'Hotel Name': r.hotelName ?? '',
    Region: r.region ?? '',
    Source: r.querySource ?? '',
    HSN: r.hsn ?? '',
    'Financial Year': r.financialYear,
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
  XLSX.writeFile(wb, `invoices-${new Date().toISOString().slice(0, 10)}.xlsx`);
};

export const InvoicesListPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const canExportFinance = useCanExportFinanceData();
  const gridState = useDataGridState({
    filterIds: FILTER_IDS as unknown as string[],
    defaultSortBy: 'createdAt',
    defaultSortDir: 'desc',
  });
  const { state } = gridState;

  // Default the view to the current financial year; the FY dropdown drives the
  // createdAt range, and a manual date-range pick switches the dropdown to blank.
  const [fy, setFy] = useState<string>(String(CURRENT_FY_START));
  const [range, setRange] = useState<UzDateRange>(() => fyRange(CURRENT_FY_START));

  const onFyChange = (value: string): void => {
    setFy(value);
    setRange(value === 'all' ? { from: null, to: null } : fyRange(Number(value)));
  };
  const onRangeChange = (r: UzDateRange): void => {
    setRange(r);
    setFy(''); // manual range = "Custom"
  };

  // Rows expanded to reveal the full details panel.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggleExpanded = useCallback(
    (id: string): void =>
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  const generate = useGenerateInvoice();
  const convertOne = useConvertToTaxInvoice();
  const convertMany = useConvertManyToTaxInvoice();

  // Proforma -> tax-invoice conversion needs a (mandatory) issue date; the
  // dialog holds the target invoice ids and the chosen date (prefilled today).
  const [convertTarget, setConvertTarget] = useState<{ ids: string[] } | null>(null);
  const [issueDate, setIssueDate] = useState<Date | null>(new Date());
  const openConvert = (ids: string[]): void => {
    setIssueDate(new Date());
    setConvertTarget({ ids });
  };
  const confirmConvert = (): void => {
    const ids = convertTarget?.ids ?? [];
    if (!issueDate || ids.length === 0) return;
    const y = issueDate.getFullYear();
    const m = String(issueDate.getMonth() + 1).padStart(2, '0');
    const d = String(issueDate.getDate()).padStart(2, '0');
    const isoDate = `${y}-${m}-${d}`;
    const done = { onSuccess: () => setConvertTarget(null) };
    if (ids.length === 1) convertOne.mutate({ id: ids[0]!, issueDate: isoDate }, done);
    else convertMany.mutate({ invoiceIds: ids, issueDate: isoDate }, done);
  };
  const converting = convertOne.isPending || convertMany.isPending;

  // Data-driven filter options (distinct agents + company bank accounts).
  const agentsQuery = useInvoiceAgents();
  const banksQuery = useBankAccountOptions();

  // Every filter is a multi-select clearable chip below the search bar. Agent and
  // bank options come from the server; the rest are fixed enums.
  const filters = useMemo((): ReadonlyArray<DataGridFilter> => {
    return [
      { id: 'status', label: 'Status', multi: true, options: STATUS_OPTIONS },
      { id: 'gstSlab', label: 'GST slab', multi: true, options: SLAB_OPTIONS },
      { id: 'placeOfSupply', label: 'Place of supply', multi: true, options: PLACE_OPTIONS },
      { id: 'customerType', label: 'Customer type', options: CUSTOMER_TYPE_OPTIONS },
      {
        id: 'agent',
        label: 'Agent',
        multi: true,
        searchable: true,
        options: (agentsQuery.data ?? []).map((a) => ({ value: a, label: a })),
      },
      {
        id: 'bank',
        label: 'Bank account',
        multi: true,
        searchable: true,
        options: (banksQuery.data ?? []).map((b) => ({ value: b.id, label: b.label })),
      },
    ];
  }, [agentsQuery.data, banksQuery.data]);

  // Filters shared by the paginated list and the "export all" walk. Multi chips
  // store comma-separated values, passed straight through to the API.
  const queryFilters = {
    sortBy: 'createdAt' as const,
    sortDir: state.sortDir,
    status: state.filters.status || undefined,
    gstSlab: state.filters.gstSlab || undefined,
    placeOfSupply: state.filters.placeOfSupply || undefined,
    customerType: (state.filters.customerType || undefined) as 'B2B' | 'B2C' | undefined,
    travelAgentName: state.filters.agent || undefined,
    bankAccountId: state.filters.bank || undefined,
    q: state.q || undefined,
    from: range.from ? startOfDayIso(range.from) : undefined,
    to: range.to ? endOfDayIso(range.to) : undefined,
  };

  const listQuery = useInvoicesList({
    page: state.page,
    pageSize: state.pageSize,
    ...queryFilters,
  });

  const handleExport = async (): Promise<void> => {
    try {
      const all = await fetchAllInvoicesForExport(queryFilters);
      exportInvoicesToXlsx(all);
    } catch {
      toast.error('Failed to export invoices');
    }
  };

  const handleExportPdf = async (): Promise<void> => {
    try {
      const all = await fetchAllInvoicesForExport(queryFilters);
      downloadPdf(
        all,
        [
          { header: 'Invoice No.', value: (r) => r.invoiceNumber },
          { header: 'Date', value: (r) => formatDateDMY(r.createdAt) },
          { header: 'Status', value: (r) => statusLabel(r.status) },
          { header: 'Customer', value: (r) => r.customerName ?? '' },
          { header: 'Agent', value: (r) => r.travelAgentName ?? '' },
          { header: 'Slab', value: (r) => r.gstSlab },
          { header: 'GST', value: (r) => gstTotalInr(r) },
          { header: 'Grand Total', value: (r) => formatInrPaise(r.grandTotalPaise) },
          { header: 'Paid', value: (r) => formatInrPaise(r.amountPaidPaise) },
          { header: 'Due', value: (r) => formatInrPaise(r.amountDuePaise) },
        ],
        'invoices',
        'Invoices',
      );
    } catch {
      toast.error('Failed to export invoices');
    }
  };

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<InvoiceRow>> => {
    return [
      {
        id: 'expand',
        header: '',
        width: '40px',
        truncate: false,
        cell: (row) => (
          <button
            type="button"
            aria-label={expanded.has(row.id) ? 'Collapse details' : 'Expand details'}
            aria-expanded={expanded.has(row.id)}
            className="flex h-6 w-6 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(row.id);
            }}
          >
            {expanded.has(row.id) ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ),
      },
      {
        id: 'invoiceNumber',
        header: 'Invoice No.',
        width: '170px',
        cell: (row) => (
          <span className="text-sm font-medium text-zinc-800 tabular-nums">
            {row.invoiceNumber}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Date',
        width: '120px',
        sortable: true,
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{formatDateDMY(row.createdAt)}</span>
        ),
      },
      {
        id: 'customerName',
        header: 'Customer',
        width: '220px',
        cell: (row) => <span className="text-sm text-zinc-700">{row.customerName ?? '-'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '130px',
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: 'gstSlab',
        header: 'Slab',
        width: '80px',
        cell: (row) => <span className="text-sm text-zinc-600">{row.gstSlab}</span>,
      },
      {
        id: 'gstTotal',
        header: 'GST total',
        width: '130px',
        align: 'right',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">{gstTotalInr(row)}</span>
        ),
      },
      {
        id: 'grandTotalPaise',
        header: 'Grand total',
        width: '150px',
        align: 'right',
        cell: (row) => (
          <span className="text-sm font-medium text-zinc-800 tabular-nums">
            {formatInrPaise(row.grandTotalPaise)}
          </span>
        ),
      },
    ];
  }, [expanded, toggleExpanded]);

  const canSubmit = bookingNumber.trim().length > 0 && !generate.isPending;

  const submitGenerate = (): void => {
    if (!canSubmit) return;
    generate.mutate(
      { bookingNumber: bookingNumber.trim() },
      {
        onSuccess: (res) => {
          setDialogOpen(false);
          setBookingNumber('');
          navigate(`/invoices/${res.id}`);
        },
      },
    );
  };

  return (
    <PageShell
      title="Invoices"
      description="GST tax invoices generated from bookings."
      actions={
        <div className="flex items-center gap-2">
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => void handleExportPdf()}
          >
            Export PDF
          </UzButton>
          <UzButton
            variant="outline"
            leftIcon={<FileText className="h-4 w-4" />}
            onClick={() => setDialogOpen(true)}
          >
            Generate from booking
          </UzButton>
          <UzButton
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/invoices/new')}
          >
            Create invoice
          </UzButton>
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <UzSelect
          value={fy || undefined}
          onValueChange={onFyChange}
          options={FY_OPTIONS}
          placeholder="Custom range"
          containerClassName="w-44"
        />
        <UzDateRangePicker
          value={range}
          onValueChange={onRangeChange}
          placeholder="Filter by date range"
          triggerClassName="w-64"
        />
      </div>

      <DataGrid
        gridId="invoices"
        data={rows}
        columns={columns}
        filters={filters}
        getRowId={(row) => row.id}
        selectable
        bulkActions={[
          {
            id: 'convert-to-tax',
            label: 'Convert to tax invoice',
            icon: <FileCheck2 className="h-4 w-4" />,
            onAction: (ids) => openConvert(ids),
          },
        ]}
        searchPlaceholder="Search invoice no., name, email, phone, agent..."
        onExport={handleExport}
        exportLabel="Export Excel"
        canExportOverride={canExportFinance}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        renderRowExtras={(row) =>
          expanded.has(row.id) ? (
            <InvoiceRowDetails
              row={row}
              onConvert={(id) => openConvert([id])}
              converting={converting}
            />
          ) : null
        }
        emptyTitle="No invoices yet"
        emptyDescription="Generate an invoice from a booking to see it here."
        tableLayout="auto"
      />

      <UzDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setBookingNumber('');
        }}
        title="Generate invoice"
        description="Enter the booking number (e.g. BK-2026-000123) to mint a GST proforma invoice."
        size="md"
        footer={
          <>
            <UzButton variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </UzButton>
            <UzButton
              leftIcon={<FileText className="h-4 w-4" />}
              loading={generate.isPending}
              disabled={!canSubmit}
              onClick={submitGenerate}
            >
              Generate
            </UzButton>
          </>
        }
      >
        <div className="space-y-2">
          <label htmlFor="invoice-booking-number" className="text-sm font-medium text-zinc-700">
            Booking number
          </label>
          <UzInput
            id="invoice-booking-number"
            value={bookingNumber}
            onChange={(e) => setBookingNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitGenerate();
            }}
            placeholder="BK-2026-000123"
            autoFocus
          />
        </div>
      </UzDialog>

      <UzDialog
        open={!!convertTarget}
        onOpenChange={(o) => !o && setConvertTarget(null)}
        title={
          (convertTarget?.ids.length ?? 0) > 1
            ? `Convert ${convertTarget?.ids.length} invoices to tax invoice`
            : 'Convert to tax invoice'
        }
        description="Pick the tax-invoice issue date. The invoice number's month/year and the issued date follow this. Defaults to today."
        size="md"
        footer={
          <>
            <UzButton variant="outline" onClick={() => setConvertTarget(null)}>
              Cancel
            </UzButton>
            <UzButton
              leftIcon={<FileCheck2 className="h-4 w-4" />}
              loading={converting}
              disabled={!issueDate}
              onClick={confirmConvert}
            >
              Convert
            </UzButton>
          </>
        }
      >
        <UzDatePicker label="Issue date" required value={issueDate} onValueChange={setIssueDate} />
      </UzDialog>
    </PageShell>
  );
};
